import { type LatLngBounds } from "leaflet";
import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useMap, useMapEvents } from "react-leaflet";
import { useSalePins } from "@/features/reassistSale/api/useReassistSale";
import { SaleDetailDialog } from "@/features/reassistSale/components/SaleDetailDialog";
import { SaleMapLegend } from "@/features/reassistSale/components/SaleMapLegend";
import {
  SaleMapNotice,
  type SaleMapNoticeKind,
} from "@/features/reassistSale/components/SaleMapNotice";
import { SalePinMarkers } from "@/features/reassistSale/components/SalePinMarkers";
import { type MapBounds } from "@/features/reassistSale/types";
import { isPinSearchableBounds } from "@/features/reassistSale/utils/bounds";

/** レイヤー名（レイヤーの一覧・URLパラメータ・表示切替で共通に使う）。 */
export const MUNICIPAL_DOCUMENT_LAYER_NAME = "役所資料（黒塗り）";

const toBounds = (bounds: LatLngBounds): MapBounds => ({
  south: bounds.getSouth(),
  west: bounds.getWest(),
  north: bounds.getNorth(),
  east: bounds.getEast(),
});

/**
 * 役所資料（黒塗り）レイヤー。表示範囲にある販売中の資料をピンで出し、ピンを押すと物件の詳細（購入・ダウンロード）を開く。
 * レイヤーがオンの間だけ描画する（オフの間は問い合わせない）。
 */
export const MunicipalDocumentLayer: React.FC = () => {
  const map = useMap();
  const [bounds, setBounds] = useState<MapBounds | null>(null);
  const [zoom, setZoom] = useState<number>(() => map.getZoom());
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const update = useCallback(() => {
    setBounds(toBounds(map.getBounds()));
    setZoom(map.getZoom());
  }, [map]);
  useMapEvents({ moveend: update, zoomend: update });
  useEffect(update, [update]);

  const { data, error } = useSalePins(bounds);
  const tooWide = bounds !== null && !isPinSearchableBounds(bounds);

  let kind: SaleMapNoticeKind | null = null;
  let message = "";
  if (tooWide) {
    kind = "tooWide";
    message = "地図を拡大すると、役所資料のピンが表示されます。";
  } else if (error !== undefined) {
    kind = "error";
    message =
      "役所資料のピンを読み込めませんでした。少し待ってから、もう一度お試しください。";
  } else if (data?.truncated === true) {
    kind = "truncated";
    message =
      "ピンが多いため、一部だけ表示しています。拡大すると残りも表示されます。";
  }

  return (
    <>
      <SalePinMarkers
        // 読み込みに失敗したときは、前の範囲のピンを残さない（範囲外のピンを選べてしまうため）
        pins={error === undefined ? data?.results ?? [] : []}
        onSelect={setSelectedId}
        zoom={zoom}
      />
      {/* 案内と凡例は地図の上に重ねる（地図の本体は position: relative） */}
      {createPortal(
        <>
          <SaleMapNotice kind={kind} message={message} />
          <SaleMapLegend
            heading="R.E.ASSIST 役所資料の凡例"
            labels={{ unpurchased: "未購入", purchased: "購入済み" }}
          />
        </>,
        map.getContainer()
      )}
      <SaleDetailDialog
        documentId={selectedId}
        onClose={() => {
          setSelectedId(null);
        }}
      />
    </>
  );
};
